import React, { useState } from 'react';
import {
  Typography,
  Chip,
  Box,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
  Divider,
  IconButton,
  Collapse,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { classifyTaskStatus, isCompletedTaskStatus } from '../utils/taskStatus';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

const formatMoney = (value) =>
  `$${(Number(value) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TaskSummaryTable = ({ lists, statuses, statusTypes = {}, showCompletedTasks = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedList, setExpandedList] = useState(null);
  const navigate = useNavigate();

  const statusPalette = theme.palette.statuses || {};
  const chartsPalette = theme.palette.charts || ['#0D0D0D', '#F20505', '#52525B'];

  const navigateToCategoryTasks = (listId, listName, status) => {
    const queryParams = new URLSearchParams();
    if (showCompletedTasks) {
      queryParams.set('includeCompleted', 'true');
    }

    const queryString = queryParams.toString();
    const path = `/category-tasks/${listId}/${encodeURIComponent(listName)}/${encodeURIComponent(status)}`;
    navigate(`${path}${queryString ? `?${queryString}` : ''}`);
  };

  if (!lists || lists.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No hay datos de tareas disponibles.
        </Typography>
      </Box>
    );
  }

  const getStatusType = (status, list) => list?.statusTypes?.[status] || statusTypes?.[status];

  const getStatusBackgroundColor = (status, statusType) => {
    const key = classifyTaskStatus(status, statusType);
    return statusPalette[key]?.bg || theme.palette.grey[100];
  };

  const getStatusTextColor = (status, statusType) => {
    const key = classifyTaskStatus(status, statusType);
    return statusPalette[key]?.fg || theme.palette.text.primary;
  };

  const getStatusMainColor = (status, statusType) => {
    const key = classifyTaskStatus(status, statusType);
    return statusPalette[key]?.main || theme.palette.grey[600];
  };

  const isForApprovalStatus = (status, statusType) => classifyTaskStatus(status, statusType) === 'forApproval';

  const getListStatuses = (list) => (
    statuses.filter((status) => (list.statusCounts[status] || 0) > 0)
  );

  const getGroupedListStatuses = (list) => {
    const activeStatuses = [];
    const completedStatuses = [];

    getListStatuses(list).forEach((status) => {
      const statusType = getStatusType(status, list);
      if (isCompletedTaskStatus(status, statusType)) {
        completedStatuses.push(status);
      } else {
        activeStatuses.push(status);
      }
    });

    return { activeStatuses, completedStatuses };
  };

  const preparePieChartData = () => {
    const statusTotals = {};
    statuses.forEach((status) => {
      const total = lists.reduce((sum, list) => sum + (list.statusCounts[status] || 0), 0);
      if (total > 0) statusTotals[status] = total;
    });
    return Object.keys(statusTotals).map((status) => ({
      name: status,
      value: statusTotals[status],
      color: getStatusMainColor(status, statusTypes?.[status]),
    }));
  };

  const prepareListPieChartData = (list) => {
    const data = [];
    statuses.forEach((status) => {
      const count = list.statusCounts[status] || 0;
      if (count > 0) {
        const statusType = getStatusType(status, list);
        data.push({
          name: status,
          value: count,
          color: getStatusMainColor(status, statusType),
        });
      }
    });
    return data;
  };

  const prepareListsChartData = () => {
    return lists.map((list, index) => ({
      name: list.listName.length > 15 ? list.listName.substring(0, 15) + '...' : list.listName,
      value: list.totalTasks,
      color: chartsPalette[index % chartsPalette.length],
    }));
  };

  const prepareStatusBarChartData = () => {
    const statusTotals = {};
    statuses.forEach((status) => {
      const total = lists.reduce((sum, list) => sum + (list.statusCounts[status] || 0), 0);
      if (total > 0) statusTotals[status] = total;
    });
    return Object.keys(statusTotals).map((status) => ({
      name: status,
      tareas: statusTotals[status],
      fill: getStatusMainColor(status, statusTypes?.[status]),
    }));
  };

  const renderPieChart = (data, chartTitle = 'Distribución') => {
    const chartHeight = isMobile ? 220 : 300;

    if (data.length === 0) {
      return (
        <Box sx={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No hay datos suficientes para mostrar el gráfico
          </Typography>
        </Box>
      );
    }

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
      const RADIAN = Math.PI / 180;
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      if (percent < 0.05) return null;

      return (
        <text
          x={x}
          y={y}
          fill="#FFFFFF"
          textAnchor={x > cx ? 'start' : 'end'}
          dominantBaseline="central"
          style={{ fontWeight: 600, fontSize: '12px' }}
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };

    return (
      <Box sx={{ height: chartHeight, width: '100%', mt: 2 }}>
        <Typography variant="subtitle2" align="center" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
          {chartTitle}
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedLabel}
              outerRadius={isMobile ? 70 : 100}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Legend
              layout="vertical"
              verticalAlign="middle"
              align="right"
              wrapperStyle={{ fontSize: '12px', color: theme.palette.text.secondary }}
            />
            <RechartsTooltip
              formatter={(value, name) => [
                `${value} tareas (${((value / data.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%)`,
                name,
              ]}
              contentStyle={{
                borderRadius: 8,
                border: `1px solid ${theme.palette.divider}`,
                fontSize: 12,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const renderBarChart = (data, chartTitle = 'Tareas') => {
    const chartHeight = isMobile ? 220 : 300;

    if (data.length === 0) {
      return (
        <Box sx={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No hay datos suficientes para mostrar el gráfico
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ height: chartHeight, width: '100%', mt: 2 }}>
        {chartTitle && (
          <Typography variant="subtitle2" align="center" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            {chartTitle}
          </Typography>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
            <YAxis tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
            <RechartsTooltip
              formatter={(value) => [`${value} tareas`, '']}
              contentStyle={{
                borderRadius: 8,
                border: `1px solid ${theme.palette.divider}`,
                fontSize: 12,
              }}
            />
            <Bar dataKey="tareas" nameKey="name" radius={[6, 6, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const renderStatusTile = (list, status, opts = {}) => {
    const count = list.statusCounts[status] || 0;
    if (count === 0) return null;

    const statusAmount = list.amountByStatus?.[status] || 0;
    const statusType = getStatusType(status, list);
    const bgColor = getStatusBackgroundColor(status, statusType);
    const textColor = getStatusTextColor(status, statusType);
    const isForApproval = isForApprovalStatus(status, statusType);
    const accent = getStatusMainColor(status, statusType);

    const { compact = false } = opts;

    return (
      <Box
        onClick={() => navigateToCategoryTasks(list.listId, list.listName, status)}
        role="button"
        tabIndex={0}
        sx={{
          bgcolor: bgColor,
          color: textColor,
          py: compact ? 1.25 : 2,
          px: 2,
          borderRadius: 2,
          textAlign: 'center',
          display: 'flex',
          flexDirection: compact ? 'row' : 'column',
          justifyContent: compact ? 'space-between' : 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: compact ? 'auto' : 96,
          border: '1px solid',
          borderColor: isForApproval ? theme.palette.secondary.main : 'transparent',
          boxShadow: isForApproval ? '0 0 0 2px rgba(242,5,5,0.15)' : 'none',
          position: 'relative',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3,
          },
          '&::before': isForApproval
            ? {}
            : {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: 3,
                height: '100%',
                bgcolor: accent,
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
              },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: 0.3,
            mb: compact ? 0 : 0.75,
            opacity: 0.9,
          }}
        >
          {status}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: isForApproval ? '1.85rem' : '1.5rem',
            lineHeight: 1,
          }}
        >
          {count}
        </Typography>
        {statusAmount > 0 && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              opacity: 0.85,
              mt: compact ? 0 : 0.5,
              ml: compact ? 1 : 0,
            }}
          >
            {formatMoney(statusAmount)}
          </Typography>
        )}
      </Box>
    );
  };

  const renderStatusSection = (list, sectionStatuses, label, opts = {}) => {
    if (sectionStatuses.length === 0) return null;

    const { compact = false } = opts;

    return (
      <>
        {label && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: compact ? 0.5 : 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}
              >
                {label}
              </Typography>
              <Divider sx={{ flexGrow: 1 }} />
            </Box>
          </Grid>
        )}
        {sectionStatuses.map((status) => {
          const tile = renderStatusTile(list, status, { compact });
          if (!tile) return null;
          return (
            <Grid item xs={compact ? 6 : 6} sm={compact ? undefined : 4} md={compact ? undefined : 3} lg={compact ? undefined : 2} key={`${list.listId}-${status}`}>
              {tile}
            </Grid>
          );
        })}
      </>
    );
  };

  const renderGroupedStatusTiles = (list, opts = {}) => {
    const { activeStatuses, completedStatuses } = getGroupedListStatuses(list);
    const showSectionLabels = showCompletedTasks && completedStatuses.length > 0;

    return (
      <>
        {renderStatusSection(list, activeStatuses, showSectionLabels ? 'Pendientes' : '', opts)}
        {renderStatusSection(list, completedStatuses, showSectionLabels ? 'Completadas' : '', opts)}
      </>
    );
  };

  // Vista móvil con cards expandibles
  if (isMobile) {
    return (
      <Box>
        {lists.map((list, index) => (
          <Card
            key={list.listId}
            sx={{
              mb: 2,
              borderLeft: '4px solid',
              borderLeftColor: chartsPalette[index % chartsPalette.length],
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {list.listName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Total: ${list.totalTasks}`}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label={`Monto: ${formatMoney(list.amountTotal)}`}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setExpandedList(expandedList === list.listId ? null : list.listId)}
                  >
                    {expandedList === list.listId ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={expandedList === list.listId}>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  {renderGroupedStatusTiles(list, { compact: true })}
                </Grid>

                <Box sx={{ mt: 2 }}>
                  {renderPieChart(prepareListPieChartData(list), `Distribución – ${list.listName}`)}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        ))}

        <Divider sx={{ my: 4 }} />

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Distribución por Tipo de Tarea
            </Typography>
            {renderBarChart(prepareStatusBarChartData(), '')}
          </CardContent>
        </Card>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Órdenes por Lista
            </Typography>
            {renderPieChart(prepareListsChartData(), '')}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Vista escritorio
  return (
    <Box>
      {lists.map((list, index) => {
        const accentColor = chartsPalette[index % chartsPalette.length];

        return (
          <Card
            key={list.listId}
            sx={{
              mb: 3,
              borderLeft: '4px solid',
              borderLeftColor: accentColor,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {list.listName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Total: ${list.totalTasks}`}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label={`Monto: ${formatMoney(list.amountTotal)}`}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setExpandedList(expandedList === list.listId ? null : list.listId)}
                  >
                    {expandedList === list.listId ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={expandedList === list.listId || expandedList === null}>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  {renderGroupedStatusTiles(list)}
                </Grid>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}

      <Divider sx={{ my: 4 }} />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Distribución por Estado
          </Typography>
          {renderBarChart(prepareStatusBarChartData(), '')}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Concentración global
          </Typography>
          {renderPieChart(preparePieChartData(), '')}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Órdenes por Lista
          </Typography>
          {renderPieChart(prepareListsChartData(), '')}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TaskSummaryTable;
